import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-mobile-only',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div class="max-w-md w-full bg-gray-800 rounded-2xl p-8 text-center">
        <!-- Mobile Icon -->
        <div class="mx-auto w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
        </div>
        
        <!-- Title -->
        <h1 class="text-2xl font-bold text-white mb-4">
          Mobile Only Feature
        </h1>
        
        <!-- Description -->
        <p class="text-gray-300 mb-6 leading-relaxed">
          The scanner feature is designed specifically for mobile devices and requires camera access. 
          Please use a mobile device or tablet to access this feature.
        </p>
    
        
        <!-- Action Buttons -->
        <div class="space-y-3">
          <button 
            (click)="tryAgain()"
            class="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class MobileOnlyComponent implements OnInit, OnDestroy {
  private returnUrl: string = '/scanner';
  private resizeTimeout: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Get the return URL from query params
    this.route.queryParams.subscribe(params => {
      this.returnUrl = params['returnUrl'] || '/scanner';
    });
    
    // Monitor screen size changes and auto-redirect if becomes mobile
    this.monitorScreenSizeChanges();
  }

  ngOnDestroy(): void {
    // Remove window resize listener
    window.removeEventListener('resize', this.handleResize.bind(this));
    
    // Clear any pending resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  tryAgain(): void {
    // Re-check if device is now mobile (screen size might have changed)
    if (this.isMobileDevice()) {
      // Device is now mobile - redirect to the original requested URL
      this.router.navigateByUrl(this.returnUrl);
    } else {
      // Still not mobile - refresh the page to update device info
      window.location.reload();
    }
  }

  private isMobileDevice(): boolean {
    // Check screen width - only allow mobile phone sizes (≤480px)
    if (window.innerWidth <= 480) {
      return true;
    }

    // Check user agent for mobile phones only (exclude tablets)
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Mobile phone keywords (exclude tablet indicators)
    const mobilePhoneKeywords = [
      'android', 'iphone', 'blackberry', 'windows phone', 'mobile'
    ];
    
    // Tablet keywords to exclude
    const tabletKeywords = ['ipad', 'ipod', 'tablet'];
    
    // Check if it's a mobile phone (has mobile keywords but NOT tablet keywords)
    const isMobilePhone = mobilePhoneKeywords.some(keyword => userAgent.includes(keyword));
    const isTablet = tabletKeywords.some(keyword => userAgent.includes(keyword));
    
    // Only return true if it's a mobile phone AND not a tablet
    return isMobilePhone && !isTablet;
  }

  getScreenSize(): string {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    return `${screenWidth} x ${screenHeight} pixels`;
  }

  getScreenSizeStatus(): string {
    return window.innerWidth <= 480 ? 'Mobile ✓' : 'Desktop ✗';
  }

  getScreenSizeStatusClass(): string {
    const baseClasses = 'text-xs font-semibold px-2 py-1 rounded-full';
    return window.innerWidth <= 480 
      ? `${baseClasses} bg-green-100 text-green-800`
      : `${baseClasses} bg-red-100 text-red-800`;
  }

  getUserAgent(): string {
    return navigator.userAgent;
  }

  private monitorScreenSizeChanges(): void {
    // Add window resize listener to monitor screen size changes
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    // Clear any existing timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Debounce resize events to prevent excessive redirects
    this.resizeTimeout = setTimeout(() => {
      // Check if device became mobile (screen size changed to mobile)
      if (this.isMobileDevice()) {
        // Screen size changed to mobile - auto-redirect to scanner
        this.router.navigateByUrl(this.returnUrl);
      }
      // Note: UI will automatically update via Angular change detection
      // so device info will refresh without manual reload
    }, 300); // 300ms debounce
  }
}
